import { ChangeDetectionStrategy, Component, effect, EffectRef, inject, input, InputSignal, Signal, viewChild } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { CepStateService } from '@libs/shared/common';
import { AddToCartModel, ProductModel } from '@libs/shop/models';
import { ProductDetailRepositoryService } from '@libs/shared/services';
import { ProductDetailTrackingService } from '@libs/shared/tracking';
import { of, switchMap } from 'rxjs';
import { AddToShoppingListDialogComponent } from '../shopping-list/add-to-shopping-list-dialog/add-to-shopping-list-dialog.component';
import { ProductDetailViewComponent } from './product-detail-view.component';
import { ProductDetailSkeletonComponent } from './product-detail-skeleton/product-detail-skeleton.component';

@Component({
  selector: 'ui-product-detail',
  templateUrl: './product-detail.component.html',
  styleUrl: './product-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ProductDetailSkeletonComponent,
    AddToShoppingListDialogComponent,
    ProductDetailViewComponent,
  ],
})
export class ProductDetailComponent {
  public sku: InputSignal<string> = input.required();
  public addToShoppingListDialog: Signal<AddToShoppingListDialogComponent> = viewChild.required(AddToShoppingListDialogComponent);
  public product: Signal<ProductModel | undefined> = toSignal(
    toObservable(this.sku).pipe(
      switchMap(sku => sku ? this.productRepository.getProduct(sku) : of(undefined)),
    ),
  );

  private readonly productRepository: ProductDetailRepositoryService = inject(ProductDetailRepositoryService);
  private readonly cepStateService: CepStateService = inject(CepStateService);
  private readonly productDetailTrackingService: ProductDetailTrackingService = inject(ProductDetailTrackingService);

  private readonly productEffect: EffectRef = effect(() => {
    const product: ProductModel | undefined = this.product();

    if (product) {
      this.cepStateService.setPageTitle(product.details.name);
      this.productDetailTrackingService.trackDetailView(product);
    }
  });

  public openAddToShoppingListDialog(payload: AddToCartModel<ProductModel>): void {
    this.addToShoppingListDialog().openDialogWithProduct(payload.item, payload.quantity);
  }
}
